/**
 * StructuredData - JSON-LD for SEO
 * 
 * Renders Schema.org structured data for Google rich results.
 * This helps Google understand the website content and display
 * enhanced search results (rich snippets).
 * 
 * Usage:
 *   <StructuredData type="LocalBusiness" data={bakeryData} />
 *   <StructuredData type="Product" data={productData} />
 */

import { useSiteImageOverrides } from '../hooks/useSiteImageOverrides';
import {
  BAKERY_LOCAL,
  buildBakerySchemaData,
  buildFaqSchemaData,
} from '../utils/bakeryLocal';
import { buildProductSchemaData } from '../utils/productSchema';

// ===========================================
// TYPES
// ===========================================

export type SchemaType = 
  | 'LocalBusiness' 
  | 'Bakery' 
  | 'Product' 
  | 'BreadcrumbList' 
  | 'WebSite'
  | 'Organization'
  | 'BlogPosting'
  | 'FAQPage';

interface StructuredDataProps {
  type: SchemaType;
  data: Record<string, unknown>;
}

// ===========================================
// COMPONENT
// ===========================================

/**
 * Renders JSON-LD structured data in a script tag
 * Google reads this to understand page content
 */
export function StructuredData({ type, data }: StructuredDataProps) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': type,
    ...data
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema, null, 0) }}
    />
  );
}

// ===========================================
// PRE-BUILT SCHEMAS
// ===========================================

/**
 * Nanny B's Bakery business information
 * Use on homepage and contact page
 */
export function BakerySchema() {
  const { getImage } = useSiteImageOverrides();
  const origin = BAKERY_LOCAL.siteUrl;
  const image = getImage('schema_storefront', `${origin}${BAKERY_LOCAL.storefrontPath}`);
  const logo = getImage('schema_logo', `${origin}${BAKERY_LOCAL.logoPath}`);
  return <StructuredData type="Bakery" data={buildBakerySchemaData(image, logo)} />;
}

/** FAQ rich results — use on /bakery-in-barrie */
export function FaqSchema() {
  return <StructuredData type="FAQPage" data={buildFaqSchemaData()} />;
}

/**
 * Product schema for individual menu items
 * Use on menu page for each product
 */
interface ProductSchemaProps {
  id: string;
  name: string;
  description?: string;
  price: number;
  image?: string;
  isAvailable?: boolean;
  category?: string;
}

export function ProductSchema({ 
  id, 
  name, 
  description, 
  price, 
  image, 
  isAvailable = true,
  category 
}: ProductSchemaProps) {
  const { getImage } = useSiteImageOverrides();
  const data = buildProductSchemaData({
    id,
    name,
    description,
    price,
    image: image || getImage('schema_placeholder', `${BAKERY_LOCAL.siteUrl}${BAKERY_LOCAL.storefrontPath}`),
    isAvailable,
    category,
  });

  return <StructuredData type="Product" data={data} />;
}

/**
 * Breadcrumb schema for navigation
 * Helps Google understand site structure
 */
interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export function BreadcrumbSchema({ items }: BreadcrumbSchemaProps) {
  const data = {
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  };

  return <StructuredData type="BreadcrumbList" data={data} />;
}

/**
 * Website schema - use once in the app
 * Helps with site search features
 */
export function WebsiteSchema() {
  // No SearchAction — the public menu has no ?search= box. Fake sitelinks search hurts SEO.
  const data = {
    name: "Nanny B's Bakery",
    url: BAKERY_LOCAL.siteUrl,
    publisher: {
      '@id': BAKERY_LOCAL.schemaId,
    },
  };

  return <StructuredData type="WebSite" data={data} />;
}

const PUBLISHER = {
  '@type': 'Organization' as const,
  name: "Nanny B's Bakery",
  url: 'https://nannybbakery.ca',
  logo: {
    '@type': 'ImageObject',
    url: 'https://nannybbakery.ca/assets/images/branding/logo2.webp',
  },
};

/** Guide / blog article — use on /guides/:slug for article rich results. */
export function ArticleSchema({
  title,
  description,
  slug,
  publishedAt,
  updatedAt,
  imageUrl,
}: {
  title: string;
  description?: string;
  slug: string;
  publishedAt?: string | null;
  updatedAt?: string | null;
  imageUrl?: string | null;
}) {
  const pageUrl = `https://nannybbakery.ca/guides/${slug}`;
  const data: Record<string, unknown> = {
    headline: title,
    description: description || title,
    url: pageUrl,
    mainEntityOfPage: { '@type': 'WebPage', '@id': pageUrl },
    author: PUBLISHER,
    publisher: PUBLISHER,
    inLanguage: 'en-CA',
  };
  if (publishedAt) data.datePublished = publishedAt;
  if (updatedAt || publishedAt) data.dateModified = updatedAt || publishedAt;
  if (imageUrl) {
    data.image = imageUrl.startsWith('http') ? imageUrl : `https://nannybbakery.ca${imageUrl}`;
  }

  return <StructuredData type="BlogPosting" data={data} />;
}

export default StructuredData;

